import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import DataPill from "@components/pills/DataPill";
import { kybService } from "@services/apis/Kyb";
import { KybLinkRecord } from "@services/models/Kyb/GetKybLinks";
import { formatDisplayDate, parseDisplayDate } from "@utils/safeDate";
import { ColDef, GridApi } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import React, { useCallback, useMemo, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import "../../components/ag-grid/styles/AgGrid.css";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import CreateKybLink from "./Components/CreateKybLink";
import KybDialog from "./Components/KybDialog";
import KybRowActions, { KybRecordAction } from "./Components/KybRowActions";
import MerchantNameCellRenderer from "./Components/MerchantNameCellRenderer";
import SubmittedCellRenderer from "./Components/SubmittedCellRenderer";
import UserIdCellRenderer from "./Components/UserIdCellRenderer";
import useKybLinks, { kybErrorMessage } from "./hooks/useKybLinks";
import "./kybLinks.css";
import { kybStyles } from "./kybStyles";

const dateFormat: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};
type StatusFilter = "all" | "pending" | "submitted";
type MappingFilter = "all" | "assigned" | "unassigned";
type RecordAction = { action: KybRecordAction; record: KybLinkRecord };

const KybLinks: React.FC = () => {
  const { handleSuccess, handleError } = useSnackbarMessage();
  const gridApi = useRef<GridApi<KybLinkRecord> | null>(null);
  const {
    records,
    hasMore,
    totalRecords,
    isLoading,
    error,
    refreshRecords,
    refreshRecord,
    resetRecords,
    loadMoreRecords,
    updateRecord,
    removeRecord,
  } = useKybLinks();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [mapping, setMapping] = useState<MappingFilter>("all");
  const [columnFiltersActive, setColumnFiltersActive] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [recordAction, setRecordAction] = useState<RecordAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const token = secureLocalStorage.getItem("jwt");
  const jwt = typeof token === "string" ? token : null;

  const openRecordAction = useCallback(
    (action: KybRecordAction, record: KybLinkRecord) => {
      setActionError("");
      setRecordAction({ action, record });
    },
    [],
  );

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter((record) => {
      const matchesStatus =
        status === "all" ||
        Boolean(record.is_submitted) === (status === "submitted");
      const assigned = record.user_id != null && String(record.user_id) !== "";
      const matchesMapping =
        mapping === "all" || assigned === (mapping === "assigned");
      const matchesSearch =
        !query ||
        [
          record.kyb_data?.merchantName,
          record.id,
          record.user_id,
          record.user_email,
          record.user_account_type,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        );
      return matchesStatus && matchesMapping && matchesSearch;
    });
  }, [records, search, status, mapping]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      suppressMovable: true,
      cellDataType: false,
      // Let native controls keep their own Enter, arrow and Tab behavior.
      suppressKeyboardEvent: ({ event }) =>
        event.target instanceof HTMLElement &&
        Boolean(event.target.closest("input, button, a, select, textarea")),
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
    }),
    [],
  );

  const columnDefs = useMemo<ColDef<KybLinkRecord>[]>(
    () => [
      {
        headerName: "Merchant",
        colId: "merchant",
        flex: 2.1,
        minWidth: 270,
        valueGetter: (params) => params.data?.kyb_data?.merchantName ?? "",
        cellRenderer: (params: { data: KybLinkRecord }) => (
          <MerchantNameCellRenderer
            data={params.data}
            jwt={jwt}
            onUpdated={updateRecord}
          />
        ),
      },
      {
        headerName: "Status",
        colId: "status",
        flex: 0.8,
        minWidth: 125,
        valueGetter: (params) =>
          params.data?.is_submitted ? "Submitted" : "Pending",
        cellRenderer: SubmittedCellRenderer,
      },
      {
        headerName: "Email",
        field: "user_email",
        flex: 1.5,
        minWidth: 180,
        cellRenderer: (params: { value: string | null }) =>
          params.value ? (
            <span className="block truncate" title={params.value}>
              {params.value}
            </span>
          ) : (
            <span className="text-text-subtle">—</span>
          ),
      },
      {
        headerName: "Account type",
        field: "user_account_type",
        flex: 0.9,
        minWidth: 125,
        cellRenderer: (params: { value: string | null }) =>
          params.value ? (
            <DataPill value={params.value} />
          ) : (
            <span className="text-text-subtle">—</span>
          ),
      },
      {
        headerName: "User ID",
        field: "user_id",
        flex: 1.1,
        minWidth: 195,
        cellRenderer: (params: { data: KybLinkRecord }) => (
          <UserIdCellRenderer
            data={params.data}
            onUpdated={updateRecord}
            refreshRecord={refreshRecord}
          />
        ),
      },
      {
        headerName: "Created",
        colId: "created_at",
        flex: 0.95,
        minWidth: 130,
        filter: "agDateColumnFilter",
        valueGetter: (params) =>
          parseDisplayDate(params.data?.created_at ?? params.data?.createdAt),
        valueFormatter: (params) =>
          params.value
            ? formatDisplayDate(params.value, "en-IN", dateFormat)
            : "—",
        cellClass: "ag-cell-custom kyb-date-cell",
      },
      {
        headerName: "Submitted",
        colId: "submitted_at",
        flex: 0.95,
        minWidth: 130,
        filter: "agDateColumnFilter",
        valueGetter: (params) =>
          parseDisplayDate(
            params.data?.submitted_at ??
              params.data?.submittedAt ??
              params.data?.kyb_data?.submittedAt,
          ),
        valueFormatter: (params) =>
          params.value
            ? formatDisplayDate(params.value, "en-IN", dateFormat)
            : "—",
        cellClass: "ag-cell-custom kyb-date-cell",
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 142,
        minWidth: 142,
        maxWidth: 142,
        pinned: "right",
        lockPinned: true,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (params: { data: KybLinkRecord }) => (
          <KybRowActions
            data={params.data}
            jwt={jwt}
            onAction={openRecordAction}
          />
        ),
        headerClass: "ag-header-cell-custom kyb-actions-header",
      },
    ],
    [jwt, updateRecord, refreshRecord, openRecordAction],
  );

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setMapping("all");
    gridApi.current?.setFilterModel(null);
    setColumnFiltersActive(false);
  };
  const filtersActive = Boolean(
    search || status !== "all" || mapping !== "all" || columnFiltersActive,
  );

  const handleRecordAction = async () => {
    if (!recordAction || actionLoading) return;
    const { action, record } = recordAction;
    setActionLoading(true);
    setActionError("");
    try {
      if (action === "delete") {
        await kybService.deleteKybRecord(record.id);
        removeRecord(record.id);
        handleSuccess({ message: "KYB record deleted." });
      } else if (action === "unmap") {
        await kybService.unmapUserIdFromKYB(record.id);
        updateRecord({
          ...record,
          user_id: null,
          user_email: null,
          user_account_type: null,
        });
        handleSuccess({ message: "User unassigned from KYB." });
      } else {
        const response = await kybService.getInstitutionalKYB(record.id);
        const isSubmitted = !record.is_submitted;
        await kybService.saveKybData(
          response.data.kybData || {},
          isSubmitted,
          record.id,
        );
        updateRecord({ ...record, is_submitted: isSubmitted });
        handleSuccess({
          message: `KYB marked as ${isSubmitted ? "submitted" : "pending"}.`,
        });
      }
      setRecordAction(null);
      if (action === "delete") {
        // Deletion shifts server page offsets; refresh the loaded pages before loading more.
        refreshRecords();
      } else {
        void refreshRecord(record.id);
      }
    } catch (err) {
      const message = kybErrorMessage(
        err,
        "Could not update this record. Please try again.",
      );
      setActionError(message);
      handleError({ message });
    } finally {
      setActionLoading(false);
    }
  };

  const actionName =
    recordAction?.record.kyb_data?.merchantName || "Unnamed merchant";
  const nextStatus = recordAction?.record.is_submitted
    ? "pending"
    : "submitted";
  const actionTitle =
    recordAction?.action === "delete"
      ? "Delete KYB record?"
      : recordAction?.action === "unmap"
        ? "Unassign user?"
        : `Mark as ${nextStatus}?`;
  const actionDescription =
    recordAction?.action === "delete"
      ? `Permanently delete the KYB record for ${actionName} and all associated data. This cannot be undone.`
      : recordAction?.action === "unmap"
        ? `Remove user ${recordAction.record.user_id} from ${actionName}. The KYB record will be kept.`
        : `Change the submission status for ${actionName} to ${nextStatus}.`;

  return (
    <div
      className={`kyb-page flex flex-1 flex-col gap-[22px] min-h-0 h-full pt-6 pb-4 text-text-base [font-family:Inter,sans-serif] max-[900px]:gap-[18px] max-[900px]:pt-[18px] ${kybStyles.motion}`}
    >
      <header className="flex items-center justify-between gap-6 px-[2px]">
        <div className="flex flex-col items-start gap-[5px]">
          <h1 className="m-0 text-[20px] font-semibold leading-[1.3] tracking-[-0.5px]">
            KYB links
          </h1>
          <p className="m-0 text-text-subtle text-[12px] leading-[1.5] max-[900px]:max-w-[240px]">
            Create links and manage business verification.
          </p>
        </div>
        <CreateKybLink
          onCreated={() => {
            clearFilters();
            resetRecords();
          }}
        />
      </header>

      <section
        className="flex flex-1 flex-col min-h-0 overflow-hidden border border-border-subtle rounded-[10px] bg-bg-page-body"
        aria-label="KYB links"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 px-4 py-[14px] border-b border-border-subtle max-[900px]:p-3 max-[900px]:gap-2">
          <div className="flex flex-1 min-w-0 flex-wrap items-center gap-[10px] max-[900px]:gap-2">
            <label className="flex items-center gap-2 w-[310px] max-w-full min-h-[36px] px-[10px] border border-border-subtle rounded-[6px] text-text-subtle bg-bg-page-body max-[900px]:w-full">
              <span
                className="material-symbols-outlined shrink-0 text-[18px]"
                aria-hidden="true"
              >
                search
              </span>
              <input
                type="search"
                className="min-w-0 w-full px-0 py-2 border-0 bg-[transparent] text-text-base text-[12px] leading-[18px] ![outline:none] placeholder:text-text-subtle"
                aria-label="Search KYB links"
                placeholder="Search merchant, user ID or email…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <div className="relative">
              <select
                className={kybStyles.select}
                aria-label="Filter by status"
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as StatusFilter)
                }
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
              </select>
              <span className={kybStyles.selectIcon} aria-hidden="true">
                expand_more
              </span>
            </div>
            <div className="relative">
              <select
                className={kybStyles.select}
                aria-label="Filter by user assignment"
                value={mapping}
                onChange={(event) =>
                  setMapping(event.target.value as MappingFilter)
                }
              >
                <option value="all">All users</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <span className={kybStyles.selectIcon} aria-hidden="true">
                expand_more
              </span>
            </div>
            {filtersActive && (
              <button
                type="button"
                className={kybStyles.textButton}
                onClick={clearFilters}
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 self-start pt-[2px]">
            <button
              type="button"
              className={kybStyles.iconButton}
              aria-label="Refresh KYB links"
              title="Refresh links"
              disabled={isLoading}
              onClick={refreshRecords}
            >
              <span
                className={`material-symbols-outlined ${isLoading ? "animate-spin" : ""}`}
                aria-hidden="true"
              >
                refresh
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div
            className="flex items-center justify-between gap-4 px-4 py-[10px] text-text-status-red bg-bg-status-red text-[12px]"
            role="alert"
          >
            <span>{error}</span>
            <button
              type="button"
              className={kybStyles.errorTextButton}
              onClick={refreshRecords}
            >
              Try again
            </button>
          </div>
        )}

        <div className="relative flex-1 min-h-0" aria-busy={isLoading}>
          {isLoading && records.length === 0 ? (
            <AgGridSkeleton
              className="kyb-grid"
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              className="ag-theme-alpine kyb-grid overflow-hidden"
              rowData={filteredRecords}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              suppressCellFocus={false}
              getRowId={(params) => String(params.data.id)}
              onGridReady={({ api }) => {
                gridApi.current = api;
                const updateCount = () => {
                  if (api.isDestroyed()) return;
                  setDisplayedCount(api.getDisplayedRowCount());
                  setColumnFiltersActive(api.isColumnFilterPresent());
                };
                api.addEventListener("modelUpdated", updateCount);
                api.addEventListener("filterChanged", updateCount);
                updateCount();
              }}
              onBodyScroll={({ api }) => {
                const lastVisibleRow = api.getLastDisplayedRowIndex();
                const count = api.getDisplayedRowCount();
                if (lastVisibleRow >= count - 5 && hasMore && !error)
                  loadMoreRecords();
              }}
              overlayNoRowsTemplate={
                filtersActive
                  ? '<div class="p-6 text-text-base text-center"><span class="text-[14px] font-medium">No matching links</span><p class="mt-[6px] text-text-subtle text-[12px]">Try another search or clear your filters.</p></div>'
                  : '<div class="p-6 text-text-base text-center"><span class="text-[14px] font-medium">No KYB links yet</span><p class="mt-[6px] text-text-subtle text-[12px]">Create a link to start business verification.</p></div>'
              }
              style={{ width: "100%", height: "100%", minHeight: 0 }}
            />
          )}
        </div>
        <footer className="flex shrink-0 items-center justify-between min-h-[42px] gap-3 px-4 py-2 border-t border-border-subtle text-text-subtle text-[11px]">
          <span role="status" aria-live="polite">
            {isLoading && records.length === 0
              ? "Loading links…"
              : filtersActive
                ? `${displayedCount.toLocaleString()} matching ${displayedCount === 1 ? "link" : "links"}${hasMore ? " in loaded records" : ""}`
                : `${records.length.toLocaleString()}${totalRecords != null && totalRecords > records.length ? ` of ${totalRecords.toLocaleString()}` : ""} ${totalRecords === 1 || (totalRecords == null && records.length === 1) ? "link" : "links"}`}
          </span>
          {hasMore && records.length > 0 && (
            <button
              type="button"
              className={kybStyles.textButton}
              disabled={isLoading}
              onClick={loadMoreRecords}
            >
              {isLoading ? "Loading…" : "Load more"}
              <span className="material-symbols-outlined" aria-hidden="true">
                expand_more
              </span>
            </button>
          )}
        </footer>
      </section>

      {recordAction && (
        <KybDialog
          title={actionTitle}
          description={actionDescription}
          busy={actionLoading}
          onClose={() => setRecordAction(null)}
        >
          {actionError && (
            <p className={kybStyles.formError} role="alert">
              {actionError}
            </p>
          )}
          <div className={kybStyles.dialogFooter}>
            <button
              autoFocus
              data-kyb-initial-focus
              type="button"
              className={kybStyles.secondaryButton}
              disabled={actionLoading}
              onClick={() => setRecordAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={
                recordAction.action === "delete"
                  ? kybStyles.dangerButton
                  : kybStyles.primaryButton
              }
              disabled={actionLoading}
              onClick={() => void handleRecordAction()}
            >
              {actionLoading
                ? "Saving…"
                : recordAction.action === "delete"
                  ? "Delete record"
                  : recordAction.action === "unmap"
                    ? "Unassign user"
                    : `Mark as ${nextStatus}`}
            </button>
          </div>
        </KybDialog>
      )}
    </div>
  );
};

export default KybLinks;
